/**
 * Player details and team lists (R30, FIBA Art. 4.2.1, 4.3.2). See §5.6: only name, number and
 * captain may ever reach game events, reports or match downloads.
 */
import { isValidBirthDate } from '../match/category';
import { isValidPlayerNumber, type GamePlayer } from '../types';

export const MAX_TEAM_MEMBERS = 12;
export const PLAYERS_ON_COURT = 5;
export const MAX_PLAYER_NAME_LENGTH = 40;
export const MAX_REGISTRATION_ID_LENGTH = 24;

export const PLAYER_POSITIONS = ['G', 'F', 'C'] as const;
export type PlayerPosition = (typeof PLAYER_POSITIONS)[number];

export interface PlayerDetails {
  name: string;
  /** Jersey number: '0', '00' or '1' to '99'. */
  number: string;
  captain?: boolean;
  position?: PlayerPosition;
  /** 'YYYY-MM-DD'. */
  birthDate?: string;
  /** The association's own player number. */
  registrationId?: string;
  /** The last 4 digits only. A full Aadhaar number is never stored. */
  aadhaarLast4?: string;
  /** The organiser has seen the original age proof. */
  ageProofChecked?: boolean;
}

export type PlayerError =
  | 'INVALID_PLAYER_NAME'
  | 'INVALID_PLAYER_NUMBER'
  | 'INVALID_POSITION'
  | 'INVALID_BIRTH_DATE'
  | 'INVALID_REGISTRATION_ID'
  | 'INVALID_AADHAAR_LAST4';

/** Exactly 4 digits. Anything longer could be a full Aadhaar number, so it is refused. */
export function isValidAadhaarLast4(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}$/.test(value);
}

/** A run of exactly 12 digits, however spaced, alone or inside other text: a full Aadhaar number. */
export function containsFullAadhaar(value: string): boolean {
  return /(?<!\d)\d{12}(?!\d)/.test(value.replace(/[\s-]/g, ''));
}

/** The first problem with a player's details, or null. `today` is 'YYYY-MM-DD'. */
export function validatePlayer(player: PlayerDetails, today: string): PlayerError | null {
  const name = typeof player.name === 'string' ? player.name.trim() : '';
  if (name === '' || name.length > MAX_PLAYER_NAME_LENGTH || containsFullAadhaar(name)) {
    return 'INVALID_PLAYER_NAME';
  }
  if (!isValidPlayerNumber(player.number)) return 'INVALID_PLAYER_NUMBER';
  if (player.position !== undefined && !PLAYER_POSITIONS.includes(player.position)) {
    return 'INVALID_POSITION';
  }
  if (player.birthDate !== undefined && !isValidBirthDate(player.birthDate, today)) {
    return 'INVALID_BIRTH_DATE';
  }
  const id = player.registrationId;
  if (
    id !== undefined &&
    (id.trim() === '' || id.trim().length > MAX_REGISTRATION_ID_LENGTH || containsFullAadhaar(id))
  ) {
    return 'INVALID_REGISTRATION_ID';
  }
  if (player.aadhaarLast4 !== undefined && !isValidAadhaarLast4(player.aadhaarLast4)) {
    return 'INVALID_AADHAAR_LAST4';
  }
  return null;
}

/** The only details of a player a game may hold (§5.6). Written out so nothing else slips in. */
export function toGamePlayer(player: PlayerDetails): GamePlayer {
  return {
    name: player.name,
    number: player.number,
    ...(player.captain === true ? { captain: true } : {}),
  };
}

export type RosterProblem =
  | { code: 'TOO_MANY_PLAYERS'; max: number }
  | { code: 'DUPLICATE_NUMBER'; number: string }
  | { code: 'NO_CAPTAIN' }
  | { code: 'MORE_THAN_ONE_CAPTAIN' };

/** Everything wrong with a team list. '0' and '00' are different numbers (Art. 4.3.2). */
export function rosterProblems(players: readonly PlayerDetails[]): RosterProblem[] {
  const problems: RosterProblem[] = [];
  if (players.length > MAX_TEAM_MEMBERS) {
    problems.push({ code: 'TOO_MANY_PLAYERS', max: MAX_TEAM_MEMBERS });
  }
  const seen = new Set<string>();
  const repeated = new Set<string>();
  for (const { number } of players) {
    if (seen.has(number)) repeated.add(number);
    seen.add(number);
  }
  for (const number of repeated) problems.push({ code: 'DUPLICATE_NUMBER', number });
  const captains = players.filter((p) => p.captain === true).length;
  if (players.length > 0 && captains === 0) problems.push({ code: 'NO_CAPTAIN' });
  if (captains > 1) problems.push({ code: 'MORE_THAN_ONE_CAPTAIN' });
  return problems;
}
